<template>
    <div v-if="!eventLoading && event" class="d-flex flex-row">
        <div class="leftcolumn minwidth600">
            <div class="mainimagecontainer mb-4">
                <img class="mainimage" :src="event.backgroundImage || getDefaultBackground()" />
            </div>

            <Paper class="mb-4">
                <div class="p-4 text-break" v-html="event.description"></div>
            </Paper>
        </div>

        <div class="flex-grow-1">
            <Paper class="p-4 mb-4">
                <div class="d-flex flex-column">
                    <div
                        v-if="!!company && !!company.logo"
                        class="d-flex flex-row justify-content-center mb-3"
                    >
                        <router-link :to="boothLink">
                            <img :src="company.logo" class="logo" />
                        </router-link>
                    </div>
                    <div class="eventtitle mb-4">{{ event.title }}</div>

                    <div class="eventinfoheader">{{ $t('vfair.events.date') }}</div>
                    <div class="eventinfovalue">{{ date }}</div>

                    <div class="eventinfoheader">{{ $t('vfair.events.time') }}</div>
                    <div class="eventinfovalue">{{ time }}</div>

                    <div class="eventinfoheader">{{ $t('vfair.events.status') }}</div>

                    <div v-if="live">
                        <div class="my-3">
                            <span class="live">
                                {{ $t('vfair.events.liveNow') }}
                            </span>
                        </div>

                        <button
                            v-if="event.url"
                            class="joinbutton btn btn-success w-100"
                            @click="eventClick"
                        >
                            {{ $t('vfair.events.joinTheEvent') }}
                        </button>
                    </div>

                    <div v-else-if="completed" class="eventinfovalue completed">
                        {{ $t('vfair.events.completed') }}
                    </div>

                    <div v-else>
                        <Countdown
                            caption="Starts in"
                            :timestamp="startTimeStamp"
                            :plainText="true"
                            class="eventinfovalue"
                        />

                        <div class="eventinfoheader mb-3">
                            ({{ $t('vfair.events.linkWillShow') }})
                        </div>

                        <AddToCalendarButton
                            v-if="event"
                            :dateStart="startTimeStamp"
                            :dateEnd="endTimeStamp"
                            :title="event.title"
                            :description="stripHtml(event.description)"
                        />
                    </div>
                </div>
            </Paper>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import Countdown from '../components/Countdown';
import AddToCalendarButton from '../components/AddToCalendarButton';

import defaultBackground from '~/assets/img/vcf/event-default-bg.png';
import AnalyticService from '../../../services/AnalyticService';
import CompanyService from '~/services/CompanyService';
import { uuidFromURLParam } from '~/utils/urlUtils';

export default {
    name: 'Event',
    components: {
        Countdown,
        AddToCalendarButton,
    },
    data() {
        return {
            live: false,
            completed: false,
            interval: null,

            company: null,
        };
    },
    computed: {
        vFairId() {
            return uuidFromURLParam(this.$route.params.id);
        },
        eventData() {
            if (!this.$route.params.webinarId) {
                return undefined;
            } else {
                return this.$interfaces.vfair
                    .id(this.vFairId)
                    .events.id(this.$route.params.webinarId)
                    .get({
                        reactive: true,
                    });
            }
        },
        event() {
            if (Array.isArray(this.eventData.data)) return undefined;
            return this.eventData.data;
        },
        eventLoading() {
            return this.eventData.loading;
        },
        date() {
            const timestamp = new moment(this.event.startTime);
            return timestamp.format('D MMMM YYYY');
        },
        startTimeStamp() {
            return new Date(this.event.startTime);
        },
        endTimeStamp() {
            return new Date(this.event.endTime);
        },
        time() {
            const timestamp = new moment(this.event.startTime);
            return timestamp.format('H:mm');
        },
        pavilionId() {
            return this.$interfaces.vfairModule.getPavilionId();
        },

        boothLink() {
            if (!this.company) return '#';

            let base = this.$URLS.vfair
                .concat('/', this.$route.params.id)
                .concat('/booths/', this.company.organizationId);

            this.$route.query.pavilion_id &&
                (base += `?pavilion_id=${this.$route.query.pavilion_id}`);

            return base;
        },
    },
    methods: {
        fetchCompany(companyId) {
            if (!!this.company) return;

            const vFairId = this.vFairId;
            if (!vFairId || !companyId) return;

            CompanyService.getVFairCompany(vFairId, companyId).then(
                res => (this.company = res.data)
            );
        },

        timerTick() {
            if (this.event) {
                const start = new moment(this.event.startTime);
                const end = new moment(this.event.endTime);
                this.live = moment().isBetween(start, end);

                this.completed = moment().isAfter(this.event.endTime);
            }
        },
        getDefaultBackground() {
            return defaultBackground;
        },
        eventClick() {
            AnalyticService.eventClickEvent(
                this.$route.params.webinarId,
                this.vFairId,
                this.event.ownerOrganizationId,
                null,
                this.pavilionId
            );
            window.open(this.event.url, '_blank');
        },
        stripHtml(html) {
            if (!html) return '';
            const el = document.createElement('div');
            el.innerHTML = html;
            return el.innerText;
        },
    },
    watch: {
        event: {
            immediate: true,
            handler(event) {
                if (event) {
                    this.fetchCompany(event.ownerOrganizationId);
                    AnalyticService.eventViewEvent(
                        this.$route.params.webinarId,
                        this.vFairId,
                        event.ownerOrganizationId,
                        null,
                        this.pavilionId
                    );
                }
            },
        },
    },
    mounted() {
        this.timerTick();
        this.interval = setInterval(this.timerTick, 1000);
    },
    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    },
};
</script>

<style scoped>
.leftcolumn {
    width: 65%;
    margin-right: 22px;
}
.mainimagecontainer {
    width: 100%;
    padding-top: 38%;
    position: relative; /* If you want text inside of it */
}

.mainimage {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
    object-fit: cover;
}
.eventtitle {
    font-size: 18px;
    text-align: center;
}
.eventinfoheader {
    text-transform: uppercase;
    font-size: 12px;
    color: #b7b7b7;
}
.eventinfovalue {
    font-size: 14px;
    color: #323c53;
    margin-bottom: 1em;
}
.logo {
    width: 80%;
    height: 80px;
    object-fit: contain;
}
.live {
    background: #f00;
    color: #fff;
    text-transform: uppercase;
    padding: 4px 6px 2px 6px;
}
.completed {
    color: #f00;
}
.joinbutton {
    color: #fff !important;
}
.minwidth600 {
    min-width: 600px;
}
</style>
